import { ILocalStorageRepository } from "Infrastructure/interfaces/ILocalStorageRepository";
import { getGlobalAddLog } from "../appInsights/AppInsights";
import { LocalStorageRepository } from "./_LocalStorageRepository";
import { Order } from "domains/order/agreggates/order/Order";
import { PedidoPos, responseSyncPedidoPos } from "../../interfaces/PedidoMeep";
import { getOrderRepository } from "./getOrderRepository";
import { ILocalStorageCashlessOfflineRepository } from "../../../domains/order/interfaces/repositories/ILocalStorageCashlessOfflineRepository";

const KEY = "@meep/CashlessOffline";

const isDev = process.env.NODE_ENV === "development";
const devLog = (...args: unknown[]) => {
  if (isDev) console.log("[LocalStorageCashlessOffline]", ...args);
};

const addLog = getGlobalAddLog();
const localStorageRepository: ILocalStorageRepository =
  LocalStorageRepository();
const orderRepository = getOrderRepository();

export const LocalStorageCashlessOfflineRepository = (): ILocalStorageCashlessOfflineRepository => {
  const getNotSincronizedOfflineRecharges = async (): Promise<
    PedidoPos[]
  > => {
    devLog("getNotSincronizedOfflineRecharges: início");
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    devLog("getNotSincronizedOfflineRecharges: dados brutos do storage", raw);
    const storagePedidosPOS: PedidoPos[] =
      raw?.filter(
        (it) => !it.sincronized
      ) ?? [];
    devLog("getNotSincronizedOfflineRecharges: filtrados (não sincronizados e não confirmados)", storagePedidosPOS);
    addLog &&
      addLog("[CashlessOfflineRepository]", {
        message: "Pendent Record confirmation",
        storagePedidosPOS,
      });
    return storagePedidosPOS;
  };

  const getNotSincronizedOfflineRechargesAndNotRecorded = async (): Promise<
    PedidoPos[]
  > => {
    devLog("getNotSincronizedOfflineRechargesAndNotRecorded: início");
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    const storagePedidosPOS: PedidoPos[] =
      raw?.filter(
        (it) => !it.sincronized && !it.isOfflineRechargeRecordConfirmed
      ) ?? [];
    devLog("getNotSincronizedOfflineRechargesAndNotRecorded: resultado", storagePedidosPOS);
    return storagePedidosPOS;
  };

  const getSincronizedOfflineRechargesAndNotRecorded = async (): Promise<
    PedidoPos[]
  > => {
    devLog("getSincronizedOfflineRechargesAndNotRecorded: início");
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    const storagePedidosPOS: PedidoPos[] =
      raw?.filter(
        (it) => it.sincronized && !it.isOfflineRechargeRecordConfirmed
      ) ?? [];
    devLog("getSincronizedOfflineRechargesAndNotRecorded: resultado", storagePedidosPOS);
    return storagePedidosPOS;
  };

  const removeRecordedConfirmationOrderFromStorageQueueAsync = async (
    responseIds: responseSyncPedidoPos[]
  ) => {
    devLog("removeRecordedConfirmationOrderFromStorageQueueAsync: início", { responseIds });
    const storagePedidosPos = await localStorageRepository.get<PedidoPos[]>(
      KEY
    );
    devLog("removeRecordedConfirmationOrderFromStorageQueueAsync: pedidos no storage", storagePedidosPos);
    const newPendentStoragePedidosPos: PedidoPos[] = [];
    storagePedidosPos?.forEach((storagePedido) => {
      if (
        responseIds.findIndex(
          (responseIds) =>
            responseIds.pedidoPOSId === storagePedido.pedidoPOSId
        ) === -1
      ) {
        newPendentStoragePedidosPos.push(storagePedido);
      } else {
        devLog("removeRecordedConfirmationOrderFromStorageQueueAsync: confirmando ordem", storagePedido.pedidoPOSId);
        orderRepository.add(
          {
            id: storagePedido.pedidoPOSId,
            isOfflineRechargeRecordConfirmed: true,
          } as Order,
          "Confirmação da gravação de recarga"
        );
      }
    });
    devLog("removeRecordedConfirmationOrderFromStorageQueueAsync: removendo KEY e salvando pendentes", newPendentStoragePedidosPos);
    await localStorageRepository.remove(KEY);
    addLog &&
      addLog("[CashlessOfflineRepository]", {
        message: "UpdateStorage",
        newPendentStoragePedidosPos,
      });
    await savePendentConfirmationRecordOfflineOrders(
      newPendentStoragePedidosPos
    );
    devLog("removeRecordedConfirmationOrderFromStorageQueueAsync: fim");
  };

  const savePendentConfirmationRecordOfflineOrders = async (
    pedidosPOS: PedidoPos[]
  ) => {
    devLog("savePendentConfirmationRecordOfflineOrders: início", { pedidosPOS });
    const storagePedidosPOS: PedidoPos[] =
      (await localStorageRepository.get<PedidoPos[]>(KEY)) ?? [];
    devLog("savePendentConfirmationRecordOfflineOrders: dados atuais no storage", storagePedidosPOS);
    const newPendentOrder = [...storagePedidosPOS, ...pedidosPOS];
    devLog("savePendentConfirmationRecordOfflineOrders: novo array a salvar", newPendentOrder);
    addLog &&
      addLog("[CashlessOfflineRepository]", {
        message: "Add Orders",
        newPendentOrder,
      });
    await localStorageRepository.post(KEY, newPendentOrder);
    devLog("savePendentConfirmationRecordOfflineOrders: fim");
  };

  /**
   * Marca um pedido de recarga offline como sincronizado com o servidor.
   * Define `sincronized = true` no pedido correspondente ao `orderId` no localStorage,
   * indicando que o backend já recebeu e processou este pedido.
   */

  //se estiver sincronizado e ja foi gravado deve excluir o pedido do storage
  const markAsSincronized = async (orderId: string) => {
    const storagePedidosPOS = await localStorageRepository.get<PedidoPos[]>(KEY);
    if (storagePedidosPOS && storagePedidosPOS?.length > 0) {
      const index = storagePedidosPOS.findIndex(
        (item) => item.pedidoPOSId === orderId
      );
      if (index >= 0) {
        storagePedidosPOS[index].sincronized = true;
      }
    }
    const filteredStoragePedidosPOS = storagePedidosPOS?.filter(
      (item) => item.isOfflineRechargeRecordConfirmed !== true || item.sincronized !== true
    );
    if (filteredStoragePedidosPOS) {
      await localStorageRepository.post(KEY, filteredStoragePedidosPOS ?? []);
    }
  };

  /**
   * Marca um pedido de recarga offline como gravado no cartão físico.
   * Define `isOfflineRechargeRecordConfirmed = true` e volta `sincronized = false`,
   * forçando o pedido a ser re-sincronizado com o backend carregando a confirmação
   * de que o saldo foi efetivamente escrito no cartão.
   * Deve ser chamado somente após o `writeBalance` do cartão ter sido bem-sucedido.
   */
  const markAsRecorded = async (orderId: string) => {

    let storagePedidosPOSConfirmationRecord =
      await localStorageRepository.get<PedidoPos[]>(KEY);
    if (storagePedidosPOSConfirmationRecord && storagePedidosPOSConfirmationRecord?.length > 0) {
      const index = storagePedidosPOSConfirmationRecord.findIndex(
        (item) => item.pedidoPOSId === orderId
      );

      if (index >= 0) {
        storagePedidosPOSConfirmationRecord[index].isOfflineRechargeRecordConfirmed = true;
        storagePedidosPOSConfirmationRecord[index].sincronized = false;

        await localStorageRepository.remove(KEY);
        await savePendentConfirmationRecordOfflineOrders(
          storagePedidosPOSConfirmationRecord
        );

      }
    }
    addLog &&
      addLog("[CashlessOfflineRepository]", {
        message: "Update Orders",
        storagePedidosPOSConfirmationRecord,
      });
  };


  const getSincronizedOfflineRechargesAndRecorded = async (): Promise<
    PedidoPos[]
  > => {
    devLog("getSincronizedOfflineRechargesAndRecorded: início");
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    const storagePedidosPOS: PedidoPos[] =
      raw?.filter(
        (it) => it.sincronized && it.isOfflineRechargeRecordConfirmed
      ) ?? [];
    devLog("getSincronizedOfflineRechargesAndRecorded: resultado", storagePedidosPOS);
    return storagePedidosPOS;
  };

  const getPedidoPosById = async (id: string): Promise<PedidoPos | null> => {
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    return raw?.find((it) => it.pedidoPOSId === id) ?? null;
  };

  const getByAssociationId = async (associationId: string): Promise<PedidoPos[] | null> => {
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    return raw?.filter((it) => it.clienteEstabelecimentoObject.pedidoPosPaiId?.toLocaleLowerCase() === associationId.toLocaleLowerCase()) ?? [];
  };

  const getNotSincronizedOfflineRechargesAndNotRecordedByAssociationId = async (associationId: string): Promise<PedidoPos[] | null> => {
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    return raw?.filter((it) =>
      it.clienteEstabelecimentoObject.pedidoPosPaiId?.toLocaleLowerCase() === associationId.toLocaleLowerCase()
      && !it.sincronized
      && !it.isOfflineRechargeRecordConfirmed
      && !it.isClosed) ?? null;
  };

  const getNotSincronizedOfflineRechargesAndNotRecordedByTag = async (tag: string): Promise<PedidoPos[] | null> => {
    const raw = await localStorageRepository.get<PedidoPos[]>(KEY);
    return raw?.filter((it) => it.tagCashless?.toLocaleLowerCase() === tag.toLocaleLowerCase() && !it.sincronized && !it.isOfflineRechargeRecordConfirmed) ?? null;
  };

  //local storage ordersId to confirm record new key @meep/CashlessOfflineConfirmationRecord
  const KEY_CONFIRMATION_RECORD = "@meep/CashlessOfflineConfirmationRecord";
  const getConfirmationRecordOfflineOrders = async (): Promise<string[]> => {
    const raw = await localStorageRepository.get<string[]>(KEY_CONFIRMATION_RECORD);
    return raw ?? [];
  };
  const addConfirmationRecordOfflineOrders = async (ordersId: string[]): Promise<void> => {
    const raw = await localStorageRepository.get<string[]>(KEY_CONFIRMATION_RECORD);
    const newOrdersId = [...(raw ?? []), ...ordersId];
    await localStorageRepository.post(KEY_CONFIRMATION_RECORD, newOrdersId);
  };
  const removeConfirmationRecordOfflineOrders = async (ordersId: string[]): Promise<void> => {
    const raw = await localStorageRepository.get<string[]>(KEY_CONFIRMATION_RECORD);
    const newOrdersId = (raw ?? []).filter((it) => !ordersId.includes(it));
    await localStorageRepository.post(KEY_CONFIRMATION_RECORD, newOrdersId);
  };

  return {
    getPedidoPosById,
    getByAssociationId,
    getNotSincronizedOfflineRecharges,
    getNotSincronizedOfflineRechargesAndNotRecorded,
    removeRecordedConfirmationOrderFromStorageQueueAsync,
    savePendentConfirmationRecordOfflineOrders,
    getSincronizedOfflineRechargesAndNotRecorded,
    getSincronizedOfflineRechargesAndRecorded,
    markAsSincronized,
    markAsRecorded,
    getNotSincronizedOfflineRechargesAndNotRecordedByAssociationId,
    getNotSincronizedOfflineOpenAccountAndNotRecordedByTag: getNotSincronizedOfflineRechargesAndNotRecordedByTag,
    removeConfirmationRecordOfflineOrders,
    addConfirmationRecordOfflineOrders,
    getConfirmationRecordOfflineOrders,
  };
};
